import React, { useRef, useState, useEffect, useCallback } from 'react';

const TabScrollWrapper = ({ children }) => {
    const wrapperRef = useRef(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const getScrollContainer = () => {
        return wrapperRef.current ? wrapperRef.current.querySelector('.dependentTabRow') : null;
    };

    const checkScroll = useCallback(() => {
        const el = getScrollContainer();
        if (!el || window.innerWidth >= 600) {
            setShowLeft(false);
            setShowRight(false);
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = el;
        // Show left arrow if scrolled to the right (scrollLeft > 1)
        setShowLeft(scrollLeft > 1);
        // Show right arrow if there is scrollable content remaining to the right
        setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = getScrollContainer();
        if (!el) return;

        // Perform initial check
        checkScroll();

        // Listen for scroll events on the tab container
        el.addEventListener('scroll', checkScroll);

        // Listen for window resize
        window.addEventListener('resize', checkScroll);

        // ResizeObserver to detect layout shifts or updates inside the tab container
        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                checkScroll();
            });
            resizeObserver.observe(el);
            // Also observe children to react if their width changes
            Array.from(el.children).forEach(child => resizeObserver.observe(child));
        }

        return () => {
            el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [children, checkScroll]);

    const handleScroll = (direction) => {
        const el = getScrollContainer();
        if (!el) return;
        const scrollAmount = 150;
        el.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };

    return (
        <div className="tab-scroll-wrapper-outer" ref={wrapperRef}>
            {showLeft && (
                <button
                    type="button"
                    className="tab-scroll-arrow left"
                    onClick={() => handleScroll('left')}
                    aria-label="Scroll left"
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                </button>
            )}
            {children}
            {showRight && (
                <button
                    type="button"
                    className="tab-scroll-arrow right"
                    onClick={() => handleScroll('right')}
                    aria-label="Scroll right"
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default TabScrollWrapper;
